import React, { useEffect, useState } from 'react';
import { Header, Message, Table } from 'semantic-ui-react';
import { CheckCircle2, Info, MinusCircle, XCircle } from 'lucide-react';
import { I18n, Translate } from 'react-redux-i18n';
import { formatYearsMonthsDays } from '../../utils/time/formatYearsMonthsDays';
import { getAccountRetention, getFlagRetention } from './retentionApi';
import { AccountRetention, AccountRetentionRule } from './types';
import { retentionRules } from './constants';
import RetentionSkeleton from './RetentionSkeleton';

// Opened either from an account (accountId) or from a row in the retention grid (flagId).
// The flag route is what lets subjects with no account of their own - guest donors and ticket
// buyers, leads, event ambassadors - use this same panel.
interface RetentionPanelProps {
  accountId?: number;
  flagId?: number;
  // Only needed alongside flagId, so the loading skeleton knows whether to leave room for the
  // login rule. An accountId always implies an account.
  hasAccount?: boolean;
}

const i18nRoot = 'account.account-detail.retentionPanel';

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : I18n.t(`${i18nRoot}.never`);

const ruleIcon = (rule: AccountRetentionRule) => {
  if (!rule.lastActivityAt) {
    return (
      <MinusCircle
        size={16}
        color="var(--color-text-secondary, #757575)"
        aria-hidden="true"
      />
    );
  }
  return rule.withinRetention ? (
    <CheckCircle2
      size={16}
      color="var(--color-success, #2e7d32)"
      role="img"
      aria-label={I18n.t(`${i18nRoot}.active`)}
    />
  ) : (
    <XCircle
      size={16}
      color="var(--color-error, #d32f2f)"
      role="img"
      aria-label={I18n.t(`${i18nRoot}.expired`)}
    />
  );
};

const eligibleLine = (retention: AccountRetention) => {
  const params = {
    years: retention.retentionYears,
    date: formatDate(retention.eligibleAt)
  };
  const age = formatYearsMonthsDays(retention.eligibleAt);
  return age
    ? I18n.t(`${i18nRoot}.status.eligibleAgo`, { ...params, age })
    : I18n.t(`${i18nRoot}.status.eligible`, params);
};

const flaggedLine = (retention: AccountRetention) => {
  if (!retention.flaggedAt) {
    return I18n.t(`${i18nRoot}.status.notFlagged`);
  }
  const date = formatDate(retention.flaggedAt);
  const age = formatYearsMonthsDays(retention.flaggedAt);
  return age
    ? I18n.t(`${i18nRoot}.status.flaggedAgo`, { date, age })
    : I18n.t(`${i18nRoot}.status.flagged`, { date });
};

// accountCreated is not in retentionRules because it substitutes for login rather than adding a
// row, so it falls through to the dynamic key here.
const ruleLabel = (rule: AccountRetentionRule) => {
  const known = retentionRules.find((r) => r.rule === rule.rule);
  return I18n.t(known ? known.labelKey : `${i18nRoot}.rule.${rule.rule}`);
};

const ruleText = (rule: AccountRetentionRule) => {
  if (!rule.lastActivityAt) {
    return I18n.t(`${i18nRoot}.never`);
  }
  const age = formatYearsMonthsDays(rule.lastActivityAt);
  return age
    ? `${formatDate(rule.lastActivityAt)} (${age})`
    : formatDate(rule.lastActivityAt);
};

const RetentionPanel = ({
  accountId,
  flagId,
  hasAccount
}: RetentionPanelProps) => {
  const [data, setData] = useState<AccountRetention | null>(null);
  const [error, setError] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    setShowStatus(false);
    if (accountId == null && flagId == null) {
      setError(true);
      return;
    }
    const request =
      accountId != null
        ? getAccountRetention(accountId)
        : getFlagRetention(flagId as number);
    request
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, flagId]);

  if (error) {
    return null;
  }

  return (
    <div>
      <Header as="h4">
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Translate value={`${i18nRoot}.title`} />
          {data && (
            <button
              type="button"
              onClick={() => setShowStatus(!showStatus)}
              aria-expanded={showStatus}
              aria-label={I18n.t(`${i18nRoot}.moreInfo`)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                padding: 0
              }}
            >
              <Info
                size={16}
                color={
                  data.isEligible
                    ? 'var(--color-error, #d32f2f)'
                    : 'var(--color-success, #2e7d32)'
                }
                aria-hidden="true"
              />
            </button>
          )}
        </span>
      </Header>
      {!data ? (
        <RetentionSkeleton
          hasAccount={accountId != null || hasAccount === true}
        />
      ) : (
        <React.Fragment>
          {showStatus && (
            <Message
              positive={!data.isEligible}
              negative={data.isEligible}
              size="small"
            >
              {data.isEligible ? (
                <React.Fragment>
                  <p>{eligibleLine(data)}</p>
                  <p>{flaggedLine(data)}</p>
                </React.Fragment>
              ) : (
                <p>
                  {I18n.t(`${i18nRoot}.status.active`, {
                    date: formatDate(data.eligibleAt)
                  })}
                </p>
              )}
            </Message>
          )}
          <Table basic="very" compact size="small" unstackable>
            <Table.Body>
              {data.rules.map((rule) => (
                <Table.Row key={rule.rule}>
                  <Table.Cell>{ruleLabel(rule)}</Table.Cell>
                  <Table.Cell>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {ruleIcon(rule)} {ruleText(rule)}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </React.Fragment>
      )}
    </div>
  );
};

export default RetentionPanel;
